import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { base_url } from "../utils/axiosConfig";
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiLock, FiEye, FiEyeOff, FiCheck, FiX, FiSmartphone } from 'react-icons/fi';

const Resetpassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // phoneNumber passed from Forget Password page via router state
  const phoneNumber = location.state?.phoneNumber || '';

  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (otp.length < 4) {
      toast.error('Please enter a valid OTP.');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${base_url}user/reset-password-otp`, {
        phoneNumber,
        otp,
        password,
      });

      toast.success('Password reset successfully! Redirecting in 5 seconds...');

      setOtp('');
      setPassword('');
      setConfirmPassword('');
      formRef.current?.reset();

      let countdown = 5;
      const interval = setInterval(() => {
        countdown -= 1;
        if (countdown > 0) {
          toast.dismiss();
          toast(`Redirecting to login in ${countdown} seconds...`);
        }
      }, 1000);

      setTimeout(() => {
        clearInterval(interval);
        setLoading(false);
        navigate('/login');
      }, 5000);
    } catch (err) {
      setLoading(false);
      toast.error(err.response?.data?.message || 'Failed to reset password. The OTP may have expired.');
    }
  };

  const isMinLength = password.length >= 6;
  const isMatching = password && password === confirmPassword;

  return (
    <div
      className="d-flex align-items-center justify-content-center py-5 px-3"
      style={{
        minHeight: '80vh',
        background: '#f8fafc',
        fontFamily: "'Outfit', sans-serif"
      }}
    >
      <div
        className="card border-0 shadow-lg position-relative overflow-hidden"
        style={{
          maxWidth: '440px',
          width: '100%',
          borderRadius: '24px',
          backgroundColor: '#ffffff'
        }}
      >
        {/* Top brand gradient bar */}
        <div style={{ height: '6px', background: 'linear-gradient(90deg, #ff6701, #fb923c)' }} />

        {loading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center z-3"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(4px)' }}
          >
            <div className="spinner-border text-primary mb-3" style={{ color: '#ff6701' }} role="status" />
            <span className="fw-semibold text-secondary">Securing your account...</span>
          </div>
        )}

        <div className="card-body p-4 p-sm-5">
          {/* Header */}
          <div className="text-center mb-4">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #fff7ed, #ffedd5)',
                color: '#ff6701'
              }}
            >
              <FiLock size={28} />
            </div>
            <h3 className="fw-bold text-dark mb-1" style={{ fontSize: '1.4rem' }}>Reset Password</h3>
            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
              Enter the OTP sent to&nbsp;
              <strong className="text-dark">{phoneNumber || 'your phone'}</strong>&nbsp;and set a new password.
            </p>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="d-flex flex-column gap-3">

            {/* Field: OTP */}
            <div>
              <label
                htmlFor="otp"
                className="form-label fw-semibold text-secondary mb-1"
                style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}
              >
                OTP
              </label>
              <div className="position-relative">
                <span
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                  style={{ zIndex: 10 }}
                >
                  <FiSmartphone size={16} />
                </span>
                <input
                  type="text"
                  className="form-control fw-medium"
                  id="otp"
                  placeholder="Enter OTP"
                  maxLength={6}
                  inputMode="numeric"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  required
                  disabled={loading}
                  style={{
                    padding: '12px 16px 12px 40px',
                    fontSize: '0.9rem',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    backgroundColor: '#f8fafc',
                    boxShadow: 'none',
                    letterSpacing: '4px',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff6701'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
            </div>

            {/* Field: New Password */}
            <div>
              <label
                htmlFor="password"
                className="form-label fw-semibold text-secondary mb-1"
                style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}
              >
                NEW PASSWORD
              </label>
              <div className="position-relative">
                <span
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                  style={{ zIndex: 10 }}
                >
                  <FiLock size={16} />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className="form-control fw-medium"
                  id="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  style={{
                    padding: '12px 42px 12px 40px',
                    fontSize: '0.9rem',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    backgroundColor: '#f8fafc',
                    boxShadow: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff6701'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="btn border-0 position-absolute end-0 top-50 translate-middle-y me-2 p-1 text-muted"
                  style={{ zIndex: 10, background: 'transparent' }}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            {/* Field: Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="form-label fw-semibold text-secondary mb-1"
                style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}
              >
                CONFIRM NEW PASSWORD
              </label>
              <div className="position-relative">
                <span
                  className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                  style={{ zIndex: 10 }}
                >
                  <FiLock size={16} />
                </span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control fw-medium"
                  id="confirmPassword"
                  placeholder="Re-enter new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  style={{
                    padding: '12px 42px 12px 40px',
                    fontSize: '0.9rem',
                    borderRadius: '12px',
                    border: '1.5px solid #e2e8f0',
                    backgroundColor: '#f8fafc',
                    boxShadow: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff6701'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="btn border-0 position-absolute end-0 top-50 translate-middle-y me-2 p-1 text-muted"
                  style={{ zIndex: 10, background: 'transparent' }}
                >
                  {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            {/* Validation Checklist */}
            <div className="d-flex flex-column gap-1 p-2 rounded-3 bg-light border" style={{ fontSize: '0.8rem' }}>
              <div className="d-flex align-items-center gap-2">
                {isMinLength ? (
                  <FiCheck className="text-success" strokeWidth={3} />
                ) : (
                  <FiX className="text-danger" strokeWidth={3} />
                )}
                <span className={isMinLength ? "text-success fw-medium" : "text-muted"}>
                  At least 6 characters
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                {isMatching ? (
                  <FiCheck className="text-success" strokeWidth={3} />
                ) : (
                  <FiX className="text-danger" strokeWidth={3} />
                )}
                <span className={isMatching ? "text-success fw-medium" : "text-muted"}>
                  Passwords match
                </span>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="btn btn-primary fw-bold text-uppercase w-100 py-3 mt-2 shadow-sm"
              disabled={loading || !isMinLength || !isMatching || otp.length < 4}
              style={{
                background: (loading || !isMinLength || !isMatching || otp.length < 4)
                  ? '#cbd5e1'
                  : 'linear-gradient(135deg, #ff6701, #fb923c)',
                border: 'none',
                borderRadius: '12px',
                fontSize: '0.85rem',
                letterSpacing: '0.8px',
                transition: 'all 0.3s ease'
              }}
            >
              {loading ? (
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span className="spinner-border spinner-border-sm" role="status" />
                  <span>Updating...</span>
                </div>
              ) : (
                'Secure Account'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Resetpassword;
